import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency as formatCurrencyUtil } from '@/lib/utils/currency';

interface BrandOverviewTabProps {
  selectedBrand: any;
  brandMetrics: any;
  loadingMetrics: boolean;
  formatCurrency?: (amount: number | string) => string;
  formatROI: (roi: number) => string;
}

export const BrandOverviewTab: React.FC<BrandOverviewTabProps> = ({
  selectedBrand,
  brandMetrics,
  loadingMetrics,
  formatCurrency,
  formatROI,
}) => {
  const { userCountry } = useAuth();

  const displayCurrency = (amount: number | string) => {
    if (formatCurrency) return formatCurrency(amount);
    return formatCurrencyUtil(amount, userCountry);
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800">Total Campaigns</h3>
          <p className="text-2xl font-bold text-blue-900">
            {loadingMetrics ? '-' : brandMetrics?.totalCampaigns || 0}
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-purple-800">Active Campaigns</h3>
          <p className="text-2xl font-bold text-purple-900">
            {loadingMetrics ? '-' : brandMetrics?.activeCampaigns || 0}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-green-800">Total Spent</h3>
          <p className="text-2xl font-bold text-green-900">
            {loadingMetrics ? '-' : displayCurrency(brandMetrics?.totalSpent || 0)}
          </p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-orange-800">Avg ROI</h3>
          <p className="text-2xl font-bold text-orange-900">
            {loadingMetrics ? '-' : brandMetrics?.averageROI ? formatROI(brandMetrics.averageROI) : 'N/A'}
          </p>
        </div>
      </div>

      {/* Brand Information */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Brand Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Industry</p>
            <p className="text-gray-900">{selectedBrand.industry || 'Not specified'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Website</p>
            {selectedBrand.websiteUrl ? (
              <a href={selectedBrand.websiteUrl} className="text-blue-600 hover:underline">
                {selectedBrand.websiteUrl}
              </a>
            ) : (
              <p className="text-gray-900">Not specified</p>
            )}
          </div>
        </div>
        {selectedBrand.description && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-500">Description</p>
            <p className="text-gray-900 mt-1">{selectedBrand.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

